import { Box, Card, Chip, CircularProgress, Typography } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PropTypes from "prop-types";
import ResourceProgressBar from "./ResourceProgressBar";
import {
  ACCENT,
  FONT,
  HAIRLINE,
  HAIRLINE_STRONG,
  INK,
  INK_MUTED,
  INK_SECONDARY,
  STATUS,
} from "../../../theme/tokens";

const KIB_PER_GIB = 1024 * 1024;

function formatWalltimeHours(hours) {
  if (!Number.isFinite(hours) || hours <= 0) return "N/A";
  return `${hours}h`;
}

function getQuotaBarProps(quota, title) {
  if (!quota) return null;

  if (quota.unit === "millicores") {
    return {
      title,
      used: quota.used / 1000,
      total: quota.nominal / 1000,
      unit: "cores",
    };
  }

  if (quota.unit === "kibibytes") {
    return {
      title,
      used: quota.used / KIB_PER_GIB,
      total: quota.nominal / KIB_PER_GIB,
      unit: "GiB",
    };
  }

  return {
    title,
    used: quota.used,
    total: quota.nominal,
    unit: title === "GPUs" ? "GPUs" : "",
  };
}

function utilizationColor(percentage) {
  if (percentage >= 90) return STATUS.error;
  if (percentage >= 70) return STATUS.warning;
  return STATUS.success;
}

// One restrained status indicator — assignment is the only status that earns
// color on the tile; everything else stays muted. Exported so the group
// header (ComputeResources.jsx) can show the same badge once per group,
// instead of repeating it on every partition tile inside it.
export function AssignmentBadge({ isAssigned }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.625,
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          bgcolor: isAssigned ? STATUS.success : HAIRLINE_STRONG,
        }}
      />
      <Typography
        sx={{
          fontSize: FONT.xs,
          fontWeight: 600,
          color: isAssigned ? STATUS.success : INK_MUTED,
          letterSpacing: "0.01em",
          whiteSpace: "nowrap",
        }}
      >
        {isAssigned ? "Assigned" : "Unassigned"}
      </Typography>
    </Box>
  );
}

AssignmentBadge.propTypes = {
  isAssigned: PropTypes.bool.isRequired,
};

// Plain descriptive text about the partition's hardware.
function HardwareLabel({ gpuFamily, gpuMemoryGb, partitionType }) {
  const isGpu = Boolean(gpuFamily) || partitionType === "gpu";

  const label = isGpu
    ? [
        gpuFamily,
        typeof gpuMemoryGb === "number" ? `${gpuMemoryGb.toFixed(0)}GB` : null,
      ]
        .filter(Boolean)
        .join(" · ") || "GPU Servers"
    : "CPU Servers";

  return (
    <Typography sx={{ fontSize: FONT.sm, color: INK_SECONDARY }}>
      {label}
    </Typography>
  );
}

HardwareLabel.propTypes = {
  gpuFamily: PropTypes.string,
  gpuMemoryGb: PropTypes.number,
  partitionType: PropTypes.string,
};

// Headline gauge for the partition's GPUs — the primary decision driver on a
// GPU cluster, so it gets the most visual weight in the tile.
function GpuGauge({ used, total }) {
  const safeTotal = Number.isFinite(total) && total > 0 ? total : 0;
  const safeUsed = Number.isFinite(used) ? Math.min(used, safeTotal) : 0;
  const pct = safeTotal > 0 ? (safeUsed / safeTotal) * 100 : 0;
  const free = Math.max(0, Math.round(safeTotal - safeUsed));
  const color = utilizationColor(pct);
  const size = 92;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <Box sx={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
        <CircularProgress
          variant="determinate"
          value={100}
          size={size}
          thickness={4}
          sx={{ color: HAIRLINE }}
        />
        <CircularProgress
          variant="determinate"
          value={pct}
          size={size}
          thickness={4}
          sx={{
            color,
            position: "absolute",
            left: 0,
            "& .MuiCircularProgress-circle": { strokeLinecap: "round" },
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: FONT.xl,
              fontWeight: 700,
              lineHeight: 1,
              color: INK,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {free}
          </Typography>
          <Typography sx={{ fontSize: FONT.sm, color: INK_MUTED, mt: 0.25 }}>
            free
          </Typography>
        </Box>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: FONT.xs, color: INK_SECONDARY }}>
          GPUs available
        </Typography>
        <Typography
          sx={{
            fontSize: FONT.lg,
            fontWeight: 600,
            color: INK,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {free}{" "}
          <span style={{ color: INK_MUTED, fontWeight: 400 }}>
            / {Math.round(safeTotal)}
          </span>
        </Typography>
      </Box>
    </Box>
  );
}

GpuGauge.propTypes = {
  used: PropTypes.number,
  total: PropTypes.number,
};

export default function GpuResourceCard({ card }) {
  const cpuBar = getQuotaBarProps(card.cpu, "CPU");
  const memoryBar = getQuotaBarProps(card.memory, "Memory");
  const gpuBar =
    card.gpu && card.gpu.nominal > 0
      ? getQuotaBarProps(card.gpu, "GPUs")
      : null;

  return (
    <Card
      variant={card.isAssigned ? "whiteCardColorfulShadow" : "outlined"}
      sx={{
        p: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        borderColor: HAIRLINE,
        ...(card.isAssigned && {
          borderLeft: `3px solid ${ACCENT}`,
        }),
      }}
    >
      {/* Header: partition name + hardware description. Group identity,
          access type, and assignment status all live one level up on the
          group card that contains this tile, so they aren't repeated here. */}
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: FONT.lg,
            fontWeight: 600,
            color: INK,
            lineHeight: 1.25,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={card.partitionName}
        >
          {card.partitionName}
        </Typography>
        <HardwareLabel
          gpuFamily={card.gpuFamily}
          gpuMemoryGb={card.gpuMemoryGb}
          partitionType={card.partitionType}
        />
      </Box>

      <Box sx={{ borderTop: `1px solid ${HAIRLINE}` }} />

      {/* Body: live utilization or empty state */}
      {card.hasLiveData ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.75,
            flex: 1,
          }}
        >
          {gpuBar && <GpuGauge used={gpuBar.used} total={gpuBar.total} />}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {cpuBar && (
              <ResourceProgressBar
                title={cpuBar.title}
                used={cpuBar.used}
                total={cpuBar.total}
                unit={cpuBar.unit}
              />
            )}
            {memoryBar && (
              <ResourceProgressBar
                title={memoryBar.title}
                used={memoryBar.used}
                total={memoryBar.total}
                unit={memoryBar.unit}
              />
            )}
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            py: 2,
          }}
        >
          <Typography sx={{ fontSize: FONT.xs, color: INK_MUTED }}>
            No live queue data
          </Typography>
        </Box>
      )}

      <Box sx={{ borderTop: `1px solid ${HAIRLINE}` }} />

      {/* Footer: walltime limits — the one fact that still varies per
          partition even within a single group */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          flexWrap: "wrap",
        }}
      >
        <AccessTimeIcon sx={{ fontSize: 14, color: INK_MUTED }} />
        <Typography sx={{ fontSize: FONT.xs, color: INK, flexShrink: 0 }}>
          Time Limits
        </Typography>
        <Chip
          label={`Workspace: ${formatWalltimeHours(card.maxWalltimeInteractive)}`}
          size="small"
          variant="outlined"
          sx={{
            height: 22,
            borderColor: HAIRLINE_STRONG,
            bgcolor: "transparent",
            color: INK,
            fontSize: FONT.xs,
            "& .MuiChip-label": { px: 0.875 },
          }}
        />
        <Chip
          label={`Job: ${formatWalltimeHours(card.maxWalltimeBatch)}`}
          size="small"
          variant="outlined"
          sx={{
            height: 22,
            borderColor: HAIRLINE_STRONG,
            bgcolor: "transparent",
            color: INK,
            fontSize: FONT.xs,
            "& .MuiChip-label": { px: 0.875 },
          }}
        />
      </Box>
    </Card>
  );
}

GpuResourceCard.propTypes = {
  card: PropTypes.shape({
    partitionName: PropTypes.string.isRequired,
    isAssigned: PropTypes.bool,
    partitionType: PropTypes.string,
    gpuFamily: PropTypes.string,
    gpuMemoryGb: PropTypes.number,
    cpu: PropTypes.shape({
      unit: PropTypes.string,
      used: PropTypes.number,
      nominal: PropTypes.number,
    }),
    memory: PropTypes.shape({
      unit: PropTypes.string,
      used: PropTypes.number,
      nominal: PropTypes.number,
    }),
    gpu: PropTypes.shape({
      unit: PropTypes.string,
      used: PropTypes.number,
      nominal: PropTypes.number,
    }),
    hasLiveData: PropTypes.bool.isRequired,
    maxWalltimeInteractive: PropTypes.number,
    maxWalltimeBatch: PropTypes.number,
  }).isRequired,
};
